import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { DialogContent_ } from "./Internal/DialogContent";
import { useDialogMode } from "./DialogModeContext";
import { InsideDialogContext } from "./Internal/InsideDialogContext";
import { useDialogHost } from "./DialogHostMutationContext";
import { DialogDirtyContext } from "./FormDirtyContext";
import { DialogInner_ } from "./Internal/DialogInner";
import { useDefaultDialogFooter } from "./Internal/useDefaultDialogFooter";
import {
  DIALOG_EXIT_MS,
  dialogWidthValues,
  type DialogProps,
  type DialogInlineProps,
} from "./Internal/dialogConstants";
import { DialogResult } from "./DialogResult";

/**
 * Dialog renders DialogInner_ directly in the parent's React tree, so state
 * updates from inside the dialog (e.g. typing in a TextInput) reach the DOM
 * in a single render cycle. No round-trip through DialogHost setState, no
 * controlled-input cursor desync.
 *
 * The DialogHost slot system is used in only one scenario: when the parent
 * unmounts Dialog while open=true (key change, conditional render
 * `{open && <Dialog />}`, route navigation). In that case Radix Presence
 * cannot animate exit because the React tree is being torn down — so the
 * unmount cleanup pushes a closed snapshot into a slot on DialogHost, which
 * survives the parent unmount, plays the exit animation for DIALOG_EXIT_MS,
 * then clears.
 *
 * The normal close path (open: true → false while parent stays mounted) does
 * not touch the slot system at all: DialogInner_ stays mounted in place,
 * Radix Presence handles the close animation directly.
 *
 * Inline mode (Storybook) bypasses everything and renders statically.
 *
 * Tray-management lives one layer up: there is intentionally no `tray` prop
 * on this component. Tray dialogs are opened via `<Button dialog={...} tray>`
 * (sugar over `tray.openElement`) or `tray.open(Component, props)`. Either
 * way the dialog wrapper is instantiated *inside* the tray host's React
 * tree, so its state (form drafts, validation) lives there and inputs
 * reconcile in a single tree — no propsRef trick, no per-keystroke bump.
 * DialogInner_ detects "I'm tray-managed" via `useDialogTrayEntry()` and
 * activates chip rendering / peek-as-park automatically.
 */
export function Dialog(props: DialogProps) {
  const mode = useDialogMode();
  if (mode === "inline") {
    return <DialogInline_ {...props} />;
  }
  return <DialogModal_ {...props} />;
}

function DialogModal_(props: DialogProps) {
  const id = useId();
  const host = useDialogHost();
  // Latest props for the unmount cleanup. Refs are safe to write in render,
  // and we want the freshest value when cleanup fires.
  const propsRef = useRef(props);
  propsRef.current = props;

  // Tripwire for the "screen killed its own open dialog" bug class: a
  // state-controlled dialog dies silently when any ancestor unmounts (a
  // full-screen loading gate during refresh, a list row replaced by a
  // refetch, a route change). A legitimate close always asks first via
  // onOpenChange(false) — so an unmount while open with no close request
  // means the owner tore the dialog down and its state is lost.
  const closeRequestedRef = useRef(false);
  if (props.open) {
    closeRequestedRef.current = false;
  }
  const wrappedProps = {
    ...props,
    onOpenChange: (open: boolean) => {
      if (!open) closeRequestedRef.current = true;
      props.onOpenChange?.(open);
    },
  };

  // Only relevant when the parent unmounts Dialog while it's still open
  // (conditional render or route change). In that case, push a closed
  // snapshot to DialogHost so Radix Presence can animate exit on a tree
  // that survives the parent unmount. In the normal close path
  // (open=false while parent stays mounted), this cleanup is a no-op
  // because the open guard skips the snapshot.
  useLayoutEffect(() => {
    return () => {
      if (!host.mounted || !propsRef.current.open) return;
      if (!closeRequestedRef.current) {
        console.warn(
          `Dialog "${propsRef.current.title ?? "(untitled)"}" was unmounted while open without a close request. ` +
            "Its owner's subtree was torn down (full-screen loading gate? row remount? navigation?), so the dialog's state is lost. " +
            "Keep the owner mounted (background refresh instead of a loading gate), hoist the dialog above the gate, or close it via onOpenChange(false) first.",
        );
      }
      const closing = { ...propsRef.current, open: false };
      host.mount(id, <DialogInner_ {...closing} stackId={id} />);
      setTimeout(() => {
        // Test environments may tear down jsdom while this timer is still
        // pending; React's setState would crash on a missing `window`.
        if (typeof window === "undefined") return;
        host.unmount(id);
      }, DIALOG_EXIT_MS);
    };
  }, [host, id]);

  return <DialogInner_ {...wrappedProps} stackId={id} />;
}

function DialogInline_(props: DialogInlineProps) {
  const {
    open = false,
    onOpenChange,
    children,
    footer,
    width = "default",
    title,
    description,
    warning,
    headerFilters,
    headerActions,
    menu,
    filterBar,
    onOk,
    onCancel,
    okLoading = false,
    okText = "OK",
    cancelText = "Cancel",
    okDisabledIfPristine = false,
    okDisabled = false,
    okDisabledMessage,
  } = props;

  // Mirror the production dirty/pristine logic so Storybook snapshots show
  // exactly what the user sees: a Form's Save button is disabled until the
  // user touches a field, a confirmation dialog's primary action stays
  // enabled.
  const [isDirty, setIsDirty] = useState(false);
  const markDirty = useCallback(() => {
    if (onOk) setIsDirty(true);
  }, [onOk]);

  const onCancelClick = useCallback(() => {
    onCancel?.();
    onOpenChange?.(false);
  }, [onCancel, onOpenChange]);
  const onOkClick = useCallback(async () => {
    const result = await onOk?.();
    if (result === DialogResult.KeepOpen) return;
    setIsDirty(false);
    onOpenChange?.(false);
  }, [onOk, onOpenChange]);
  const finalFooter = useDefaultDialogFooter({
    footer,
    onOk,
    onCancel,
    okLoading,
    okText,
    cancelText,
    onOkClick,
    onCancelClick,
    okDisabled: okDisabled || (okDisabledIfPristine && !isDirty),
    ...(okDisabledMessage !== undefined && { okDisabledMessage }),
  });

  // oxlint-disable-next-line fs/no-null -- React requires null for conditional rendering
  if (!open) return null;

  const titleNode = title ? (
    <h2 className="text-2xl font-bold text-foreground">{title}</h2>
  ) : undefined;
  const descriptionNode = description ? (
    <p className="mt-1 text-sm text-muted-foreground">{description}</p>
  ) : undefined;

  return (
    <DialogDirtyContext.Provider value={markDirty}>
      <InsideDialogContext.Provider value={true}>
        <div
          className={`flex flex-col ${width === "auto" ? "w-fit" : "w-full"} rounded-2xl border border-border bg-card shadow-lg`}
          style={{ maxWidth: dialogWidthValues[width] }}
        >
          <DialogContent_
            titleNode={titleNode}
            descriptionNode={descriptionNode}
            warning={warning}
            headerFilters={headerFilters}
            headerActions={headerActions}
            menu={menu}
            filterBar={filterBar}
            finalFooter={finalFooter}
          >
            {children}
          </DialogContent_>
        </div>
      </InsideDialogContext.Provider>
    </DialogDirtyContext.Provider>
  );
}
